/**
 * §7.4 «Управление ролями» (вкладка Настройки → Роли).
 *
 * Воспроизводит мокап 7.4: таблица ролей тенанта (роль · отдел/тип · кол-во
 * прав · системная да/нет · «что может» · действия), кнопка «+ Custom роль»,
 * форма создания/правки прав. Системные (платформенные) роли — read-only.
 *
 * Бэк: GET/POST /v1/admin/roles, PATCH/DELETE /v1/admin/roles/{id}
 * (useRoles / useCreateRole / useUpdateRole / useDeleteRole).
 *
 * ПАТЕНТ feedback_malformed_role_perms_drops_rbac: permissions ВСЕГДА массив
 * строк (никогда {} объект) — иначе роль молча выпадает из RBAC. Форма
 * собирает permissions через чекбоксы и отдаёт [] минимум.
 */
import { useMemo, useState } from "react";
import { Card, Skeleton } from "@hsc/ui";
import type { Role } from "@hsc/types";
import { roleKeyLabel, permissionsSummary, PERMISSION_CATALOG } from "./staff-i18n.js";
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole } from "./useRoles.js";

export interface RolesPanelProps {
  canManage?: boolean;
}

interface RoleFormState {
  mode: "create" | "edit";
  role?: Role;
}

function RoleForm({
  state,
  tenantId,
  onClose,
}: {
  state: RoleFormState;
  tenantId: string;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateRole();
  const update = useUpdateRole();
  const editing = state.mode === "edit" && state.role != null;
  const [displayName, setDisplayName] = useState(state.role?.display_name ?? "");
  const [key, setKey] = useState(state.role?.key ?? "");
  const [perms, setPerms] = useState<Set<string>>(() => new Set(state.role?.permissions ?? []));

  const togglePerm = (p: string): void =>
    setPerms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    // permissions ВСЕГДА массив (патент). Пустой набор → [].
    const permissions = Array.from(perms);
    if (editing && state.role) {
      update.mutate({ id: state.role.id, input: { permissions } }, { onSuccess: onClose });
      return;
    }
    if (key.trim() === "" || displayName.trim() === "") return;
    create.mutate(
      { tenant_id: tenantId, key: key.trim(), display_name: displayName.trim(), permissions },
      { onSuccess: onClose },
    );
  };

  const pending = create.isPending || update.isPending;
  const isError = create.isError || update.isError;

  return (
    <section className="roles-panel" data-testid="role-form-screen">
      <div className="ta-topbar">
        <h1 className="hsc-page__title">{editing ? "Изменить права роли" : "Новая роль"}</h1>
      </div>
      <Card>
        <form onSubmit={submit} className="role-form">
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="role-name">Название роли</label>
            <input
              id="role-name"
              className="ta-input"
              data-testid="role-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Напр. «Старший бариста»"
              disabled={editing}
              required={!editing}
            />
          </div>
          {!editing && (
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="role-key">Ключ (latin, для RBAC)</label>
              <input
                id="role-key"
                className="ta-input"
                data-testid="role-key-input"
                value={key}
                onChange={(e) => setKey(e.target.value.replace(/[^a-z0-9_:]/g, ""))}
                placeholder="напр. staff:barista_senior"
                required
              />
            </div>
          )}
          <fieldset className="role-form__perms">
            <legend className="hsc-input__label">Права (что может роль)</legend>
            <div className="role-form__perm-grid">
              {PERMISSION_CATALOG.map((p) => (
                <label key={p.key} className="role-form__perm">
                  <input
                    type="checkbox"
                    data-testid={`role-perm-${p.key}`}
                    checked={perms.has(p.key)}
                    onChange={() => togglePerm(p.key)}
                  />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {isError && (
            <p role="alert" className="staff-invite__msg staff-invite__msg--err">
              Не удалось сохранить роль.
            </p>
          )}
          <div className="staff-modal__actions">
            <button type="submit" className="staff-btn" disabled={pending} data-testid="role-form-save">
              {pending ? "Сохраняем…" : editing ? "Сохранить" : "Создать роль"}
            </button>
            <button type="button" className="staff-btn staff-btn--ghost" onClick={onClose} data-testid="role-form-back">Назад</button>
          </div>
        </form>
      </Card>
    </section>
  );
}

export function RolesPanel({ canManage = false }: RolesPanelProps): JSX.Element {
  const { roles, tenantId, isLoading, isError, error } = useRoles();
  const del = useDeleteRole();
  const [form, setForm] = useState<RoleFormState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Role | null>(null);

  const sorted = useMemo(
    () => [...roles].sort((a, b) => Number(b.is_platform) - Number(a.is_platform)),
    [roles],
  );

  if (isLoading) {
    return (
      <section data-testid="roles-panel-loading">
        <Skeleton height={40} radius={12} />
        <Skeleton height={200} radius={12} />
      </section>
    );
  }

  // Создание/правка роли — ОТДЕЛЬНЫЙ экран (форма вместо таблицы), не модалка
  // поверх (feedback_every_form_is_a_separate_screen).
  if (form != null && tenantId != null) {
    return <RoleForm state={form} tenantId={tenantId} onClose={() => setForm(null)} />;
  }

  if (isError) {
    const status = (error as { status?: number } | null)?.status;
    return (
      <section data-testid="roles-panel-error">
        <p role="alert" className="staff-empty">
          {status === 403
            ? "У Вашей роли нет прав на управление ролями. Обратитесь к владельцу отеля."
            : "Не удалось загрузить роли."}
        </p>
      </section>
    );
  }

  return (
    <section data-testid="roles-panel" className="roles-panel">
      <div className="dept-panel__head">
        <h2 className="staff-profile__section-title">Роли · {roles.length}</h2>
        {canManage && tenantId != null && (
          <button type="button" className="staff-btn" data-testid="role-add-btn" onClick={() => setForm({ mode: "create" })}>
            + Custom роль
          </button>
        )}
      </div>

      {roles.length === 0 ? (
        <p className="staff-empty">Кастомных ролей нет. Можете создать первую.</p>
      ) : (
        <table className="roles-table" data-testid="roles-table">
          <thead>
            <tr>
              <th>Роль</th>
              <th>Тип</th>
              <th>Прав</th>
              <th>Может</th>
              <th aria-label="действия" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} data-testid={`role-row-${r.id}`}>
                <td title={r.key}>
                  <strong>{roleKeyLabel(r.key, r.display_name)}</strong>
                  <span className="roles-table__key">{r.key}</span>
                </td>
                <td>
                  {r.is_platform ? (
                    <span className="ta-chip">системная</span>
                  ) : (
                    <span className="ta-chip ta-chip--ok">custom</span>
                  )}
                </td>
                <td>{r.permissions.length}</td>
                <td className="roles-table__can">{permissionsSummary(r.permissions)}</td>
                <td className="roles-table__actions">
                  {canManage && !r.is_platform && (
                    <>
                      <button type="button" className="staff-btn staff-btn--ghost" data-testid={`role-edit-${r.id}`} onClick={() => setForm({ mode: "edit", role: r })}>
                        Править
                      </button>
                      <button type="button" className="staff-btn staff-btn--ghost" data-testid={`role-delete-${r.id}`} onClick={() => setConfirmDelete(r)}>
                        Удалить
                      </button>
                    </>
                  )}
                  {r.is_platform && <span className="roles-table__readonly">read-only</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDelete != null && (
        <div className="staff-modal" role="dialog" aria-modal="true" data-testid="role-delete-modal">
          <div className="staff-modal__panel">
            <h3 className="staff-modal__title">Удалить роль «{roleKeyLabel(confirmDelete.key, confirmDelete.display_name)}»?</h3>
            <p className="staff-modal__text">Нельзя удалить роль, если на ней есть сотрудники. Действие необратимо.</p>
            {del.isError && (
              <p role="alert" className="staff-invite__msg staff-invite__msg--err">
                Не удалось удалить роль — возможно, на ней есть сотрудники.
              </p>
            )}
            <div className="staff-modal__actions">
              <button type="button" className="staff-btn staff-btn--ghost" onClick={() => setConfirmDelete(null)}>Отмена</button>
              <button
                type="button"
                className="staff-btn staff-btn--danger"
                data-testid="role-delete-confirm"
                disabled={del.isPending}
                onClick={() => del.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })}
              >
                {del.isPending ? "Удаляем…" : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
